import React, { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
import Lottie from 'lottie-react';
import { BGM_EVENTS, isBgmPaused, isPageBgmEnabled } from '../../utils/bgm';
import './LottiePageBackground.css';

const BGM_STREAM_URL =
  // The supplied master playlist's AAC rendition. Loading the audio-only
  // rendition avoids ending the video MediaSource after the first pass.
  'https://customer-neooj6eu5wlxrhme.cloudflarestream.com/f62c797428fdc330527563df1e430cc5/manifest/stream_tb9a8aed652490320288ff458278d1396_r1855331044.m3u8';

const BGM_FULL_VOLUME = 1;
const BGM_DUCK_VOLUME = 0.22;

export default function LottiePageBackground() {
  const [animationData, setAnimationData] = useState(null);
  const [reduceMotion, setReduceMotion] = useState(false);
  const audioRef = useRef(null);
  const hlsRef = useRef(null);
  const keepAliveRef = useRef(true);
  const userPausedRef = useRef(isBgmPaused());
  const pageEnabledRef = useRef(isPageBgmEnabled());

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (event) => setReduceMotion(event.matches);
    setReduceMotion(media.matches);
    media.addEventListener('change', onChange);

    import('../../assets/lottie/web_console_bg.json').then((mod) => {
      setAnimationData(mod.default || mod);
    });

    return () => media.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;
    keepAliveRef.current = true;

    const attemptPlay = () => {
      if (
        !keepAliveRef.current ||
        document.hidden ||
        userPausedRef.current ||
        !pageEnabledRef.current
      ) {
        return;
      }
      audio.play().catch(() => {});
    };
    const onEnded = () => {
      hlsRef.current?.stopLoad();
      audio.currentTime = 0;
      hlsRef.current?.startLoad(0);
      attemptPlay();
    };
    const onPause = () => {
      if (
        !keepAliveRef.current ||
        document.hidden ||
        userPausedRef.current ||
        !pageEnabledRef.current
      ) {
        return;
      }
      window.setTimeout(attemptPlay, 120);
    };
    const onDuck = () => {
      if (userPausedRef.current || !pageEnabledRef.current) return;
      audio.volume = BGM_DUCK_VOLUME;
    };
    const onRestore = () => {
      if (userPausedRef.current || !pageEnabledRef.current) return;
      audio.volume = BGM_FULL_VOLUME;
      attemptPlay();
    };
    const onUserPause = () => {
      userPausedRef.current = true;
      audio.pause();
    };
    const onUserPlay = () => {
      userPausedRef.current = false;
      audio.volume = BGM_FULL_VOLUME;
      attemptPlay();
    };
    const onPageOn = () => {
      pageEnabledRef.current = true;
      attemptPlay();
    };
    const onPageOff = () => {
      pageEnabledRef.current = false;
      audio.pause();
    };

    audio.volume = BGM_FULL_VOLUME;
    audio.addEventListener('canplay', attemptPlay);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('pause', onPause);
    window.addEventListener('pointerdown', attemptPlay, true);
    window.addEventListener('keydown', attemptPlay, true);
    window.addEventListener(BGM_EVENTS.RESUME, attemptPlay);
    window.addEventListener(BGM_EVENTS.DUCK, onDuck);
    window.addEventListener(BGM_EVENTS.RESTORE, onRestore);
    window.addEventListener(BGM_EVENTS.PAUSE, onUserPause);
    window.addEventListener(BGM_EVENTS.PLAY, onUserPlay);
    window.addEventListener(BGM_EVENTS.PAGE_ON, onPageOn);
    window.addEventListener(BGM_EVENTS.PAGE_OFF, onPageOff);
    document.addEventListener('visibilitychange', attemptPlay);

    if (audio.canPlayType('application/vnd.apple.mpegurl')) {
      audio.src = BGM_STREAM_URL;
      audio.load();
    } else if (Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: false,
      });
      hlsRef.current = hls;
      hls.loadSource(BGM_STREAM_URL);
      hls.attachMedia(audio);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        attemptPlay();
      });
      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (data?.fatal) {
          hls.destroy();
          hlsRef.current = null;
        }
      });
    }

    return () => {
      keepAliveRef.current = false;
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
      hlsRef.current?.destroy();
      hlsRef.current = null;
      audio.removeEventListener('canplay', attemptPlay);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('pause', onPause);
      window.removeEventListener('pointerdown', attemptPlay, true);
      window.removeEventListener('keydown', attemptPlay, true);
      window.removeEventListener(BGM_EVENTS.RESUME, attemptPlay);
      window.removeEventListener(BGM_EVENTS.DUCK, onDuck);
      window.removeEventListener(BGM_EVENTS.RESTORE, onRestore);
      window.removeEventListener(BGM_EVENTS.PAUSE, onUserPause);
      window.removeEventListener(BGM_EVENTS.PLAY, onUserPlay);
      window.removeEventListener(BGM_EVENTS.PAGE_ON, onPageOn);
      window.removeEventListener(BGM_EVENTS.PAGE_OFF, onPageOff);
      document.removeEventListener('visibilitychange', attemptPlay);
    };
  }, []);

  return (
    <div className="lottie-page-bg">
      {animationData ? (
        <Lottie
          className="lottie-page-bg__player"
          animationData={animationData}
          loop={!reduceMotion}
          autoplay={!reduceMotion}
          renderer="svg"
          rendererSettings={{ preserveAspectRatio: 'xMidYMid slice' }}
          style={{ width: '100%', height: '100%' }}
        />
      ) : null}
      <audio
        ref={audioRef}
        className="lottie-page-bg__audio"
        preload="auto"
        loop
        aria-label="Background music"
      />
    </div>
  );
}
